import React, { useState } from 'react';
import { Award, Users, Target, CheckCircle } from 'lucide-react';
import Footer from '../components/Footer';

const AwardsPage = () => {
  const [previewImage, setPreviewImage] = useState(null);

  const values = [
    {
      icon: Target,
      title: "Quality First",
      description: "We never compromise on quality. Every project reflects our commitment to excellence and precision in construction."
    },
    {
      icon: CheckCircle,
      title: "Timely Delivery",
      description: "We understand the value of time and ensure all projects are completed within the agreed timeline."
    },
    {
      icon: Users,
      title: "Customer Focus",
      description: "Our clients are at the heart of everything we do. We listen, understand, and deliver beyond expectations."
    },
    {
      icon: Award,
      title: "Innovation",
      description: "We embrace modern construction techniques and technologies to provide innovative solutions."
    }
  ];

  const awards = [
    { image: "/images/certification.jpeg", title: "Times Real Estate Award", caption: "Award received for Prajakta Ikebana — Presented by Hon'ble Shri. Nitinji Gadkari" },
    { image: "/images/PrajactaTai Award-7.jpeg", title: "Recognized for Excellence", caption: "Celebrating innovation and trust at Times Property Real Estate Awards 2022" },
    { image: "/images/PrajactaTai Award-5.jpeg", title: "Apartment Excellence Award", caption: "Honored with the 1st Position at MP Birla Cement Technocrat Awards 2024 for Apartment Building" },
    { image: "/images/PrajactaTai Award-6.jpeg", title: "Celebrating Achievement", caption: "Recognized for outstanding contribution at Times Property Awards 2022" },
    { image: "/images/PrajactaTai Award-4.jpeg", title: "Pride of Achievement", caption: "Recognized with 1st Rank in Apartment Building – MP Birla Cement Awards 2024" },
    { image: "/images/carazol5.JPG", title: "A Moment of Recognition", caption: "Honored with 1st Rank in Apartment Building at MP Birla Cement Awards 2024" },
    { image: "/images/Prajktaproject.JPG", title: "Celebrating Success", caption: "Our team being recognized for the 'Prajakta' project" },
    { image: "/images/PrajctaTai Award-3.jpeg", title: "Customer Satisfaction Award", caption: "We are honored to be recognized by the residents of Prajakta Green Serenity" },
    { image: "/images/all member .JPG", title: "Recognized for Quality and Excellence", caption: "Client-first approach in every decision" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="relative pt-24 sm:pt-28 pb-16 sm:pb-20 text-white overflow-hidden"
        style={{
          backgroundImage: "url('/images/Awards image 4.jpg')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          height: "90vh",
        }}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 sm:top-20 right-10 sm:right-20 w-48 sm:w-96 h-48 sm:h-96 bg-white rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-10 sm:left-20 w-40 sm:w-64 h-40 sm:h-64 bg-white rounded-full blur-2xl"></div>
        </div>
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center items-center text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-4 sm:mb-6">
            <span className="text-white">Our Journey</span>
            <br />
            <span className="text-white">Crowned with Awards</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white max-w-3xl sm:max-w-4xl mx-auto leading-relaxed">
            Every award is a milestone in our journey of building excellence.
          </p>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Awards &{' '}
              <span className="bg-[#011321] bg-clip-text text-transparent">
                Recognitions
              </span>
            </h1>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto">
              Celebrating our achievements and recognition in the construction industry.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {awards.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md hover:shadow-2xl transform-gpu transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                onClick={() => setPreviewImage(item.image)}
              >
                {/* Badge */}
                <div className="absolute top-3 left-3 z-20">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-[#011321] shadow">
                    <Award className="w-4 h-4" />
                    <span className="text-xs font-semibold tracking-wide">Award</span>
                  </div>
                </div>

                {/* Image */}
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-64 object-cover transform-gpu transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-70 group-hover:opacity-80 transition-opacity duration-300" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 z-10">
                  <h3 className="text-lg sm:text-xl font-bold text-white">{item.title}</h3>
                  <p className="mt-1 text-xs sm:text-sm text-red-100">{item.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🔍 Image Preview Modal (Inline) */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative w-full h-full max-w-[95vw] max-h-[95vh]">
            <img
              src={previewImage}
              alt="Preview"
              className="w-full h-full object-contain rounded-xl shadow-2xl"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 rounded-full shadow-lg hover:bg-red-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AwardsPage;
